/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CARD_GAP, REFETCH_CONFIGURATION } from "../../../utils/constants";
import BackButton from "../../feedBacks/BackButton";
import StatsCard from "../../Cards/statsCard";
import GaugeChart from "../../Charts/DTStatus";
import VoltageProfileChart from "../../Charts/VoltageProfileChart";
import { useFetchData } from "../../../customHooks/useGetDashboardData";
import { formatCustomDateDayMonthYear } from "../../../utils/utils";
import Loader2 from "../../feedBacks/loader2";
import { IDevice } from "../../../utils/interfaces";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { memo, useCallback, useEffect, useState } from "react";
import LoadProfileChart from "../../Charts/LoadProfileChart";
import DeviceDetailTable from "../../Table/DeviceDetailTable";
import CurrentProfileChart from "../../Charts/CurrentProfileChart";
import "../../../styles/custom-styles.css";
import { useUtils } from "../../../customHooks/useUtils";
import { resetFilter } from "../../../store/filterSlice";
import Loader from "../../feedBacks/loader";

function OperationSite({
  site_id,
  from_aggregate,
}: {
  site_id: string;
  from_aggregate?: boolean;
}) {
  const dispatch = useAppDispatch();
  const [isFilterReset, setIsFilterReset] = useState(false);

  useEffect(() => {
    const resetFilters = async () => {
      try {
        await dispatch(resetFilter());
        setIsFilterReset(true);
      } catch (error) {
        console.error("Error resetting filters:", error);
        // Still allow rendering even if reset fails
        setIsFilterReset(true);
      }
    };

    resetFilters();
  }, [dispatch]);

  const language = useAppSelector((state) => state.language.language);
  const startDate = useAppSelector((state) => state.filter.startDate);
  const endDate = useAppSelector((state) => state.filter.endDate);
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  // Fetch operations data with React Query
  const { mapResponseToLabelValue, INSTATAENOUS_KPIS, t } = useUtils();
  const {
    data: operationsData,
    isLoading: operationsLoading,
    error: operationsError,
    refetch,
  } = useFetchData(
    ["/site", "fetchSite", site_id],
    `/operations/site/${site_id}`,
    {
      start_date: startDate,
      end_date: endDate,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
      // Only enable query if filter has been reset
      enabled: isFilterReset && !!site_id,
    }
  );

  // Fetch site details with React Query
  const {
    data: siteDetails,
    isLoading: siteDetailIsLoading,
    error: siteDetailError,
  } = useFetchData<IDevice>(
    ["/site", "fetchSiteDetail", site_id],
    `/device/${site_id}`,
    {},
    // @ts-ignore
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      enabled: isFilterReset && !!site_id,
    }
  );

  // Fetch site status data with React Query
  const { data: siteStatusData } = useFetchData<any>(
    ["/site", "fetchSiteStatus", site_id],
    `/device-status/${site_id}`,
    {},
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
      enabled: isFilterReset && !!site_id,
    }
  );

  const isLoading =
    // Show loading for initial page load
    operationsLoading ||
    // Show loading when filter is applied and when actually refetching
    isLocalLoading ||
    // Show loading while filter is being reset
    !isFilterReset;

  useEffect(() => {
    if (startDate && endDate && isFilterReset) {
      setIsLocalLoading(true);

      refetch().finally(() => {
        setIsLocalLoading(false);
      });
    }
  }, [startDate, endDate, refetch, isFilterReset]);

  // Transform operations data using React Query's data
  const transformedData = operationsData
    ? mapResponseToLabelValue(operationsData)
    : [];

  // Render stats cards
  const renderStatsCards = useCallback(
    (dataSlice: any[], noOfLoadBox: number) => {
      if (isLoading) {
        return Array.from({ length: noOfLoadBox }).map((_, index) => (
          <StatsCard
            key={`loading-${index}`}
            title=""
            value=""
            isError={false}
            isLoading={true}
          />
        ));
      }

      if (operationsError) {
        return Array.from({ length: 4 }).map((_, index) => (
          <StatsCard
            key={`error-${index}`}
            title=""
            value=""
            isError={true}
            isLoading={false}
            cardErrorStyle="!h-[50px]"
          />
        ));
      }
      const excludeCards = [t("readings.unknown")];

      return dataSlice.map(
        (data: any, index: number) =>
          !excludeCards.includes(data.label) && (
            <div key={`stat-${index}`}>
              <StatsCard
                title={data.label}
                time={
                  INSTATAENOUS_KPIS.includes(data.label)
                    ? (operationsData as any).time
                    : ""
                }
                period={
                  !INSTATAENOUS_KPIS.includes(data.label)
                    ? formatCustomDateDayMonthYear(startDate, endDate, language)
                    : ""
                }
                value={data.value as string}
                isError={false}
                isLoading={false}
              />
            </div>
          )
      );
    },
    [
      isLoading,
      operationsError,
      operationsData,
      startDate,
      endDate,
      INSTATAENOUS_KPIS,
      t,
      language,
    ]
  );

  // Don't render the main content until filter reset is complete
  if (!isFilterReset) {
    return (
      <div className="flex justify-center items-center w-full h-64">
        <div className="flex justify-center items-center w-full h-64">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full h-full min-h-full"
      style={{ gap: CARD_GAP }}
    >
      {!from_aggregate && (
        <div className="flex w-full">
          <div className="flex gap-2 self-start justify-start items-center">
            <BackButton
              showBackText={false}
              link={`/dashboard/operations/home/${siteDetails?.company_id}`}
            />
            {siteDetailIsLoading ? (
              <Loader2 />
            ) : siteDetailError ? (
              <div className="text-red-500">
                {t("feedback_msg.error_fetching")}
              </div>
            ) : siteDetails?.asset_name ? (
              <span className="text-primary-blackMain text-xl font-bold ml-2">
                {siteDetails.asset_name}
              </span>
            ) : null}
          </div>
        </div>
      )}
      {/* site details and gauge chart */}
      <div
        className="flex flex-wrap justify-between lg:justify-start items-center w-full"
        style={{ gap: CARD_GAP }}
      >
        <div className="w-full lg:w-[450px] !lg:max-w-[450px] ">
          {
            <DeviceDetailTable
              isLoading={siteDetailIsLoading}
              data={[siteDetails as IDevice]}
              site_status={siteStatusData}
              error={siteDetailError as string}
            />
          }
        </div>
        <GaugeChart site_id={site_id} />
        <div
          className={`grid gap-4 w-full [grid-template-columns:repeat(auto-fit,minmax(250px,1fr))] ${
            window.innerWidth >= 1600 ? "!w-[auto] !min-w-[350px]" : ""
          } load-rate-custom-stat-card-style`}
          style={{
            gap: CARD_GAP,
            // gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          }}
        >
          {renderStatsCards(transformedData.slice(0, 8), 6)}
        </div>
        {/* <KeyInsights /> */}
      </div>
      {/* first stats card */}
      <div
        className="grid w-full"
        style={{
          gap: CARD_GAP,
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        }}
      >
        {renderStatsCards(transformedData.slice(8), 8)}
      </div>

      {/* Load profile charts */}
      <div
        className="flex flex-wrap justify-between w-full"
        style={{ gap: CARD_GAP }}
      >
        {/* <Load profile /> */}
        <LoadProfileChart
          params={{ start_date: startDate, end_date: endDate }}
          route={`/operations-dashboard/load-profile-chart/${site_id}`}
          queryKey={["/operations", "fetchSiteLoadProfile", site_id]}
          title={t("chart.load_profile")}
        />
      </div>
      {/* <Predicted Load profile /> */}
      {site_id === "8f0ff7e6-b487-4ff1-b990-31407a33b82cxxxxxxxxxxx" && (
        <div
          className="flex flex-wrap justify-between w-full"
          style={{ gap: CARD_GAP }}
        >
          {/* <Predicted Load profile /> */}
          <LoadProfileChart
            isPredicted={true}
            params={{ start_date: startDate, end_date: endDate }}
            route={`/operations-dashboard/load-profile-prediction-chart/${site_id}`}
            queryKey={["/operations", "fetchPredictedSiteLoadProfile", site_id]}
            title={t("chart.load_profile_predicted")}
          />
        </div>
      )}

      <div className="flex justify-between w-full" style={{ gap: CARD_GAP }}>
        <VoltageProfileChart
          queryKey={["/operations", "fetchVoltageProfile", site_id]}
          route={`/operations-dashboard/voltage-profile-chart/${site_id}`}
          title={t("chart.voltage_profile")}
        />
      </div>
      <div className="flex justify-between w-full" style={{ gap: CARD_GAP }}>
        <CurrentProfileChart
          queryKey={["/operations", "fetchCurrentProfile", site_id]}
          route={`/operations-dashboard/current-profile-chart/${site_id}`}
        />
      </div>
    </div>
  );
}

export default memo(OperationSite);
